import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../core/AuthContext'
import Button from '../components/Button'
import Input from '../components/Input'
import Logo from '../components/Logo'
import Icon from '../components/Icon'
import Spinner from '../components/Spinner'
import { validateEmail, validatePassword } from '../lib/utils'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState('login') // login or signup
  const [name, setName] = useState('')
  const navigate = useNavigate()
  const { login, signup } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Validation
      if (!validateEmail(email)) {
        throw new Error('Invalid email address')
      }

      if (mode === 'signup') {
        if (!name.trim()) {
          throw new Error('Name is required')
        }
        const passwordError = validatePassword(password)
        if (passwordError) {
          throw new Error(passwordError)
        }
        await signup(email, password, name)
      } else {
        if (!password) {
          throw new Error('Password is required')
        }
        await login(email, password)
      }

      navigate('/home')
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface-dim flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex mb-3 hover:opacity-80 transition" aria-label="Snagmatic home">
            <Logo height={40} />
          </Link>
          <p className="text-body-md text-text-secondary">
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-8">
          {error && (
            <div className="mb-4 p-3 bg-status-urgent/10 border border-status-urgent rounded-lg">
              <p className="text-status-urgent text-label-sm">{error}</p>
            </div>
          )}

          {mode === 'signup' && (
            <Input
              label="Full Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              disabled={isLoading}
              className="mb-4"
            />
          )}

          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={isLoading}
            className="mb-4"
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            disabled={isLoading}
            helperText={mode === 'signup' ? 'Min 8 chars, 1 uppercase, 1 number' : ''}
            className="mb-6"
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            disabled={isLoading}
            className="mb-4"
          >
            {isLoading ? <Spinner size="sm" /> : mode === 'login' ? 'Sign In' : 'Sign Up'}
          </Button>

          {/* Only show Google login in login mode */}
          {mode === 'login' && (
            <>
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-label-sm">
                  <span className="px-2 bg-surface text-text-muted">Or</span>
                </div>
              </div>

              <a
                href={`${import.meta.env.VITE_SNAGMATIC_API_URL || 'http://localhost:4001'}/api/snagmatic/auth/google`}
                className="w-full flex items-center justify-center gap-2 border border-border bg-surface-high hover:bg-surface-bright text-text-primary text-label-md rounded-lg py-3 transition"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </a>
            </>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="text-accent hover:underline text-label-sm"
            >
              {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </form>

        {/* Legal Links */}
        <div className="text-center mt-8 text-label-sm text-text-muted">
          <p>
            <Link to="/privacy" className="text-accent hover:underline">Privacy</Link>
            {' '} · {' '}
            <Link to="/terms" className="text-accent hover:underline">Terms</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
